import { useEffect, useRef, useState } from 'react'
import { useCamera } from '../../hooks/useCamera'
import { useFaceDetection } from '../../hooks/useFaceDetection'
import { GlassesOverlay } from './GlassesOverlay'
import { Button } from '../Button'
import type { CoverColor } from '../../types'
import styles from './TryOnCamera.module.css'

interface TryOnCameraProps {
  selectedColor: CoverColor
  onColorChange: (color: CoverColor) => void
}

const COLORS: CoverColor[] = ['red', 'yellow', 'green', 'blue']

const COLOR_LABELS: Record<CoverColor, string> = {
  red: 'Rojo',
  yellow: 'Amarillo',
  green: 'Verde',
  blue: 'Azul',
}

export function TryOnCamera({ selectedColor, onColorChange }: TryOnCameraProps) {
  const { videoRef, stream, isLoading, error: cameraError, startCamera, stopCamera } = useCamera({
    width: 640,
    height: 480,
  })

  const { landmarks, isModelLoaded, error: detectionError, processFrame } = useFaceDetection()

  const animationRef = useRef<number | null>(null)
  const [videoDimensions, setVideoDimensions] = useState({ width: 640, height: 480 })

  useEffect(() => {
    if (!stream || !videoRef.current) return

    const video = videoRef.current

    const detectFaces = async () => {
      if (video.readyState === 4) {
        await processFrame(video)
      }
      animationRef.current = requestAnimationFrame(detectFaces)
    }

    detectFaces()

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [stream, processFrame, videoRef]) // Removed isModelLoaded from dependencies

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleLoadedMetadata = () => {
      setVideoDimensions({
        width: video.videoWidth || 640,
        height: video.videoHeight || 480,
      })
    }

    video.addEventListener('loadedmetadata', handleLoadedMetadata)
    return () => video.removeEventListener('loadedmetadata', handleLoadedMetadata)
  }, [videoRef])

  const error = cameraError || detectionError

  return (
    <div className={styles.container}>
      <div className={styles.cameraWrapper}>
        {!stream ? (
          <div className={styles.placeholder}>
            <div className={styles.placeholderContent}>
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              <h3 className={styles.placeholderTitle}>Prueba Virtual</h3>
              <p className={styles.placeholderText}>
                Prueba nuestras gafas usando tu cámara
              </p>
              <Button onClick={startCamera} disabled={isLoading}>
                {isLoading ? 'Iniciando...' : 'Activar Cámara'}
              </Button>
              {error && <p className={styles.error}>{error}</p>}
            </div>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              className={styles.video}
              playsInline
              muted
              style={{
                transform: 'scaleX(-1)', // Mirror effect
              }}
            />
            {landmarks && (
              <div className={styles.overlayWrapper} style={{ transform: 'scaleX(-1)' }}>
                <GlassesOverlay
                  landmarks={landmarks}
                  videoWidth={videoDimensions.width}
                  videoHeight={videoDimensions.height}
                  selectedColor={selectedColor}
                />
              </div>
            )}
            {!isModelLoaded && (
              <div className={styles.loadingOverlay}>
                <div className={styles.spinner} />
                <p>Cargando detección facial...</p>
              </div>
            )}
          </>
        )}
      </div>

      {stream && (
        <div className={styles.controls}>
          <div className={styles.colorPicker}>
            <span className={styles.colorLabel}>Color de cubierta:</span>
            <div className={styles.colorOptions}>
              {COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`${styles.colorButton} ${styles[color]} ${selectedColor === color ? styles.selected : ''
                    }`}
                  onClick={() => onColorChange(color)}
                  aria-label={`Seleccionar ${COLOR_LABELS[color]}`}
                  aria-pressed={selectedColor === color}
                />
              ))}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={stopCamera}>
            Detener Cámara
          </Button>
        </div>
      )}

      {stream && !landmarks && isModelLoaded && (
        <p className={styles.hint}>Posiciona tu rostro frente a la cámara</p>
      )}
    </div>
  )
}
